import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { observer } from 'mobx-react-lite'
import moment from 'moment'
import { debounce } from 'lodash'
import classNames from 'classnames'
import * as qs from 'qs'

// evertel
import {
    TanTable, TanTableColumnDef,
    PageBody, Badge, Text,
    Row, Button, Card, CardBody,
    Spinner
} from '@evertel/web/ui'
import { DocumentSearchForm } from './DocumentSearchForm'
import { useService } from '@evertel/di'
import { DepartmentDocumentSchemasController, DocumentRecentTemplatesController, DocumentSearchController, DocumentSearchFilter, DocumentSchemaStore } from '@evertel/document'
import { DocumentIcon, DocumentTemplateCard, DocumentsTabs } from '../elements'
import { CurrentUserController } from '@evertel/blue-user'
import { SessionService } from '@evertel/session/SessionService'
import { useDidMountEffect } from '@evertel/hooks'
import { APIDataBlueUser, APIDataDocument } from '@evertel/types'

const MAX_DISPLAYED_SCHEMAS = 8

const DocumentSearch: React.FC = observer(() => {

    const navigate = useNavigate()
    const location = useLocation()
    const [isLoading, setIsLoading] = useState(true)

    const [searchFilter, setSearchFilter] = useState<DocumentSearchFilter>({by: null, state: 'published', text: ''})

    const documentSearchController = useService(DocumentSearchController, [])
    const documentSchemaStore = useService(DocumentSchemaStore, [])
    const departmentDocumentSchemasController = useService(DepartmentDocumentSchemasController, [])
    const documentRecentTemplatesController = useService(DocumentRecentTemplatesController, [])
    const currentUserController = useService(CurrentUserController, [])
    const sessionService = useService(SessionService, [])

    // parse query string
    useState(() => {
        documentSearchController.init({ isInfiniteScroll: false })

        // load all parameters from URL
        // parameters need to be processed before <DocumentSearchForm> is loaded
        const parsedQuery = qs.parse(location.search, { ignoreQueryPrefix: true })

        if (typeof parsedQuery.limit === 'string') {
            documentSearchController.setLimit(parseInt(parsedQuery.limit))
        }
        if (typeof parsedQuery.order === 'string') {
            documentSearchController.setOrder(parsedQuery.order)
        }
        if (typeof parsedQuery.filter === 'string') {
            try {
                setSearchFilter(JSON.parse(decodeURIComponent(parsedQuery.filter)))
            } catch { /* nothing */ }
        }
        //must be set last as all the others reset the page
        if (typeof parsedQuery.page === 'string') {
            documentSearchController.setPage(parseInt(parsedQuery.page), true)
        }
    })

    const updateQueryString = (params: any) => {
        const currentQuery = qs.parse(location.search, { ignoreQueryPrefix: true })
        const newQuery = { ...currentQuery, ...params }
        const searchParams = qs.stringify(newQuery)
        navigate(`/everdocs?${searchParams}`, { replace: true })
    }

    useEffect(() => {
        if (!documentSearchController.isLoading) {
            setIsLoading(false)
        }
    }, [documentSearchController.isLoading])


    useEffect(() => {
        // initialize (fetch available schemas and departments for search filters and table display)
        documentSearchController.fetchDocumentSchemas()

    }, [sessionService.currentUserId, sessionService.selectedDepartmentId])

    useEffect(() => {
        // department templates for the "Create an EverDoc" section
        if (sessionService.selectedDepartmentId) {
            departmentDocumentSchemasController.init(sessionService.selectedDepartmentId)
        }
    }, [sessionService.selectedDepartmentId])

    const recentTemplates = documentRecentTemplatesController.getRecentSchemas(
        departmentDocumentSchemasController.enabledDocumentSchemas, MAX_DISPLAYED_SCHEMAS
    )

    // no usage history yet — fall back to the most recently updated enabled templates
    // instead of an empty section, as long as the agency actually has any
    const displayTemplates = recentTemplates.length > 0
        ? recentTemplates
        : [...departmentDocumentSchemasController.enabledDocumentSchemas]
            .sort((a, b) => moment(b.updatedDate).valueOf() - moment(a.updatedDate).valueOf())
            .slice(0, MAX_DISPLAYED_SCHEMAS)

    useEffect(() => {
        // SEARCH (triggered each time the searchFilter changes)
        documentSearchController.setFilter(searchFilter)
        search()
    }, [searchFilter])

    useDidMountEffect(() => {
        // SEARCH (triggered each time the page/limit/order changes.)
        search()
    }, [documentSearchController.page, documentSearchController.limit, documentSearchController.order])

    const debouncedSearch = useCallback(debounce(async () => {
        setIsLoading(true)

        // update query string with each search, stripping null/empty values for a clean URL
        const cleanFilter = Object.fromEntries(
            Object.entries(documentSearchController.filter).filter(([_, v]) => v != null && v !== '')
        )
        updateQueryString({
            filter: JSON.stringify(cleanFilter),
            page: documentSearchController.page,
            limit: documentSearchController.limit,
            order: documentSearchController.order
        })

        try {
            await documentSearchController.search()
        } catch (error) {
            console.error(error.message)
        }
        //this dependency list must be empty or debounce will be created over and over and not work
    }, 500), [])

    const search = useCallback(() => {
        //setting loading to true sooner feels more responsive
        setIsLoading(true)
        debouncedSearch()
    }, [debouncedSearch])


    const filteredSchemas = useMemo(() => {
        if (searchFilter.by?.type === 'department') {
            return documentSearchController.documentSchemas.filter(
                s => s.departmentId === searchFilter.by.id
            )
        }
        return documentSearchController.documentSchemas
    }, [documentSearchController.documentSchemas, searchFilter.by])

    const onFilterChange = (filter) => {
        setSearchFilter((prev) => ({
            ...prev,
            ...filter
        }))
    }

    const handleDocumentSelect = (row: any) => {
        const document = row.original as APIDataDocument
        navigate(`/everdocs/view/${document.id}`)
    }

    const columns = useMemo<TanTableColumnDef<APIDataDocument>[]>(() => [
        {
            header: 'Name/Title',
            accessorKey: 'name',
            size: '30%',
            enableSorting: true,
            cell: ({ row }) => (
                // the td's ellipsis (truncateCells) doesn't reach into a flex row, so the
                // name shrinks (min-width: 0) and truncates itself; icon and badge keep their size
                <div className="d-flex align-items-center">
                    <DocumentIcon
                        className="me-2 flex-shrink-0"
                        schema={documentSchemaStore.findById(row.original.documentSchemaId)}
                    />
                    <strong
                        className={classNames('text-truncate', { 'text-muted': !row.original.name })}
                        title={row.original.name || undefined}>
                        {row.original.name || 'Untitled Document'}
                    </strong>
                    {row.original.isArchived && (
                        <Badge
                            className="ms-2 flex-shrink-0"
                            color="muted">
                            Archived
                        </Badge>
                    )}
                </div>
            )
        },
        {
            header: 'Type',
            accessorKey: 'documentSchemaId',
            size: '15%',
            enableSorting: true,
            cell: ({ row }) => (
                <Text size={13}>
                    {documentSchemaStore.findById(row.original.documentSchemaId)?.name}
                </Text>
            )
        },
        {
            header: 'Last Modified',
            size: '20%',
            accessorKey: 'updatedDate',
            accessorFn: (row) => moment(row.updatedDate).format('ll HH:mm'),
            enableSorting: true,
            sortDescFirst: true,
            cell: ({ row, getValue }) => (
                <Text
                    size={13}
                >
                    {getValue() as string}
                </Text>
            )
        },
        {
            id: 'acl',
            header: 'Owner',
            size: '20%',
            accessorFn: (row) => {
                const ownerAcl = row.ACL?.find(a => a.accessType === 'OWNER')
                if (!ownerAcl) return ''

                const owner = ownerAcl?.principal as APIDataBlueUser
                return (owner?.firstName?.trim() || owner?.lastName?.trim())
                    ? `${owner?.firstName || ''} ${owner?.lastName || ''}`.trim()
                    : (owner?.email || owner?.id || '??????????')
            },
            enableSorting: false
        },
        {
            id: 'agency',
            header: 'Agency',
            size: '20%',
            accessorFn: (row) => {
                return `${row?.department?.name}`
            },
            enableSorting: false,
            cell: ({ cell, getValue }) => (
                <Text size={13}>
                    {cell.getValue() as string}
                </Text>
            )
        }
    ], [documentSearchController.documentSchemaIds])

    return (
        <PageBody
            flush
            className="documents-page">
            {currentUserController.canManage &&
                <div className="px-4 pt-4">
                    <DocumentsTabs />
                </div>
            }

            {!currentUserController.hasGuestRoleInSelectedDepartment &&
                <div className="create-document-section px-4 py-4">
                    <Row
                        align="between"
                        valign="center"
                        className="mb-3">
                        {/* text column shrinks (min-width: 0) so the sub-text wraps
                            instead of pushing the button onto its own line */}
                        <div className="flex-grow-1 me-3" style={{ minWidth: 0 }}>
                            <h3>
                                Select a template to create an EverDoc
                            </h3>
                            <p className="text-muted mb-0">
                                Choose a template to create a new document. Select a recent template or browse all template categories.
                            </p>
                        </div>
                        <Button
                            color="primary"
                            className="flex-shrink-0"
                            onClick={() => navigate('/everdocs/templates')}>
                            Browse all templates
                        </Button>
                    </Row>
                    {displayTemplates.length === 0 ? (
                        <Card className="document-template-empty-card">
                            <CardBody className="d-flex justify-content-center align-items-center">
                                <span className="text-muted">
                                    {currentUserController.canManage
                                        ? 'There are no templates enabled for your agency yet. Create and enable templates so that members of your agency can create EverDocs.'
                                        : 'There are no templates available for creating an EverDoc. Executives and Management can enable templates.'}
                                </span>
                            </CardBody>
                        </Card>
                    ) : (
                        <div
                            className="document-template-card-grid">
                            {displayTemplates.map((schema) =>
                                <DocumentTemplateCard
                                    key={schema.id}
                                    schema={schema}
                                    onClick={() => navigate(`/everdocs/add/${schema.id}`)}
                                />
                            )}
                        </div>
                    )}
                </div>
            }

            <div className="px-4 py-4">
                <Row
                    valign="center"
                    className="mb-1">
                    <h3 className="mb-0">
                        All EverDocs
                    </h3>
                    <div aria-live="polite">
                        {(!isLoading) ? (
                            <Badge
                                color="primary"
                                className="ms-2"
                            >
                                {documentSearchController.documentsCount}
                                <span className="visually-hidden">{`${documentSearchController.documentsCount === 1 ? 'document' : 'documents'}`} found</span>
                            </Badge>
                        ) : (
                            <div className="d-flex align-items-center justify-content-center" style={{height: 0, overflow: 'visible'}}>
                                <Spinner width={40}/>
                                <span className="visually-hidden">Loading results</span>
                            </div>
                        )}
                    </div>
                </Row>
                <p className="text-muted">
                    These are all the documents you've created or that have been shared in the rooms you have access to.
                </p>
                <DocumentSearchForm
                    filter={searchFilter}
                    onFilterChange={onFilterChange}
                    documentDepartments={documentSearchController.documentDepartments}
                    documentSchemas={filteredSchemas}
                />
                <TanTable
                    className="table-hover"
                    remote={true}
                    loading={isLoading}
                    data={documentSearchController.documents}
                    rowCount={documentSearchController.documentsCount}
                    columns={columns}
                    options={{
                        responsive: true,
                        responsiveHeight: 'calc(100vh - 280px)',
                        truncateCells: true,
                        pageSize: documentSearchController.limit,
                        pageNumber: documentSearchController.page,
                        sortName: documentSearchController.order ? documentSearchController.order.split(' ')[0] : null,
                        sortOrder: documentSearchController.order ? documentSearchController.order.split(' ')[1] as 'asc' | 'desc' : null,
                        onPageChange: (page) => documentSearchController.setPage(page),
                        onPageSizeChange: (pageSize) => documentSearchController.setLimit(pageSize),
                        onSortChange: (sortName, sortOrder) => {
                            const newOrder = (sortOrder) ? `${sortName} ${sortOrder}` : null
                            documentSearchController.setOrder(newOrder)
                        }
                    }}
                    onRowClick={handleDocumentSelect}
                    aria-label="Documents list"
                />
            </div>
        </PageBody>
    )
})

export {
    DocumentSearch
}
